import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { css } from '@emotion/react'
import type { Board } from '@logic/board'
import { createBoard } from '@logic/board'
import type { Color, GameOverType, Move, Piece } from '@logic/pieces'
import { Environment, useProgress } from '@react-three/drei'
import { Canvas } from '@react-three/fiber'

import { BoardComponent } from '@/components/Board'
import { GameOverScreen } from '@/components/GameOverScreen'
import { Loader } from '@/components/Loader'
import { StatusBar } from '@/components/StatusBar'
import { Toast } from '@/components/Toast'
import { Border } from '@/models/Border'
import { ComputerMoveService } from '@/services/computerMove'
import { useGameSettingsState } from '@/state/game'
import { useHistoryState } from '@/state/history'

export type GameOver = {
  type: GameOverType
  winner: Color
}

const App: FC = () => {
  const [board, setBoard] = useState<Board>(createBoard())
  const [selected, setSelected] = useState<Piece | null>(null)
  const [moves, setMoves] = useState<Move[]>([])
  const [gameOver, setGameOver] = useState<GameOver | null>(null)
  const [isComputerThinking, setIsComputerThinking] = useState(false)
  
  const resetHistory = useHistoryState((state) => state.reset)
  const { resetTurn, turn, setTurn, gameStarted, setGameStarted } = useGameSettingsState((state) => ({
    resetTurn: state.resetTurn,
    turn: state.turn,
    setTurn: state.setTurn,
    gameStarted: state.gameStarted,
    setGameStarted: state.setGameStarted,
  }))

  const reset = () => {
    setBoard(createBoard())
    setSelected(null)
    setMoves([])
    resetHistory()
    resetTurn()
    setGameOver(null)
    setIsComputerThinking(false)
    setGameStarted(true) // Start game immediately in single-player mode
  }

  // Handle computer moves
  useEffect(() => {
    if (gameStarted && !gameOver && turn === 'black' && !isComputerThinking) {
      setIsComputerThinking(true)
      ComputerMoveService.makeComputerMove(board, setBoard, turn, setTurn)
        .finally(() => {
          setIsComputerThinking(false)
        })
    }
  }, [board, turn, gameStarted, gameOver, isComputerThinking, setTurn])

  const [total, setTotal] = useState(0)
  const { progress } = useProgress()
  useEffect(() => {
    setTotal(progress)
  }, [progress])

  // Start the game immediately when component loads
  useEffect(() => {
    if (total === 100 && !gameStarted) {
      setGameStarted(true)
    }
  }, [total, gameStarted, setGameStarted])

  const handleResign = () => {
    if (!gameOver) {
      setGameOver({ type: 'checkmate', winner: turn === 'white' ? 'black' : 'white' })
    }
  }

  return (
    <div
      css={css`
        height: 100vh;
        width: 100vw;
        background: linear-gradient(135deg, #10131a 0%, #1E2836 100%);
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        position: relative;
        overflow: hidden;
        box-shadow: 0 0 0 8px rgba(30, 40, 54, 0.7), 0 0 40px 0 #000a;
        &::before {
          content: '';
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: url('/chessLogo.png') center center no-repeat;
          background-size: contain;
          opacity: 0.08;
          z-index: 0;
          pointer-events: none;
        }
      `}
    >
      {total === 100 ? (
        <>
          {/* Top Right Logo */}
          <div
            css={css`
              position: absolute;
              top: 18px;
              right: 18px;
              z-index: 10;
              width: 36px;
              height: 28px;
              opacity: 0.5;
              pointer-events: none;
              transform: scaleX(-1) rotate(10deg);
            `}
          >
            <img src="/212.svg" alt="212 Logo" style={{ width: '100%', height: '100%' }} />
          </div>
          {/* Bottom Right Logo */}
          <div
            css={css`
              position: absolute;
              bottom: 18px;
              right: 18px;
              z-index: 10;
              width: 36px;
              height: 28px;
              opacity: 0.5;
              pointer-events: none;
              transform: scaleX(-1) scaleY(-1) rotate(-10deg);
            `}
          >
            <img src="/212.svg" alt="212 Logo" style={{ width: '100%', height: '100%' }} />
          </div>
          {/* Resign Button */}
          <button
            onClick={handleResign}
            disabled={!!gameOver}
            style={{
              position: 'absolute',
              top: 18,
              left: 18,
              zIndex: 20,
              padding: '10px 20px',
              fontSize: '1.2rem',
              background: 'linear-gradient(135deg, #D72A29, #931A1D)',
              color: 'white',
              border: 'none',
              borderRadius: 10,
              cursor: gameOver ? 'not-allowed' : 'pointer',
              opacity: gameOver ? 0.5 : 1,
              boxShadow: '0 4px 15px rgba(215, 42, 41, 0.4)',
            }}
          >
            Resign
          </button>
        </>
      ) : (
        <Loader />
      )}
      {/* <Sidebar board={board} moves={moves} selected={selected} /> */}
      <StatusBar />
      <GameOverScreen gameOver={gameOver} onReset={reset} />
      <Toast />
      <Canvas shadows camera={{ position: [Math.cos(3) * 13.5, 5, Math.sin(3) * 13.5], fov: 50 }}>
        <Environment files={`dawn.hdr`} />
        <Border />
        <BoardComponent
          selected={selected}
          setSelected={setSelected}
          board={board}
          setBoard={setBoard}
          moves={moves}
          setMoves={setMoves}
          setGameOver={setGameOver}
          isComputerThinking={isComputerThinking}
          gameOver={gameOver}
        />
      </Canvas>
    </div>
  )
}

export default App 